// Staff softphone web push (roadmap #41). Ported from qr-phone-frontend
// firebase.service.tsx (messaging half) + App.jsx (foreground onMessage + the
// service-worker message bridge). The FCM contract is unchanged: the token is
// registered with POST /users/fcmtoken/update and silent notifications carry the
// same action names (FCM-INCOMING-CALL, FCM-KILL-NOTIFICATION-CALL,
// FCM-LOGOUT-SESSION) and the same payload fields the backend already sends.
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { child, get, ref } from 'firebase/database'
import { app, authReady, database } from '../core/firebase'
import { globals } from '../core/globals'
import { httpRequest } from '../core/http'
import { updateCallStatusByUserId } from '../core/callStatus'
import { setStoredFcmToken, staffLogout } from './staffAuth'
import { t } from '../i18n'

// Web push certificate (VAPID public key). It is issued PER FIREBASE PROJECT, so it has
// to travel with the rest of the per-environment Firebase configuration (roadmap #12):
// a key from another project makes getToken() fail and the device silently never
// registers for call alerts. Defaults to the production key so a build with no override
// behaves exactly as before. It is a public key by design, not a secret.
const VAPID_KEY =
  import.meta.env.VITE_FIREBASE_VAPID_KEY ||
  'BF4X6ryYiTfK4dqPsQcfAiceSjmNXynFQPSoPcvEX-TGgaTyR8Ti6mxHFTIZaDvCBL1n2uGPr54fcObjPEWrKpw'

let messaging: ReturnType<typeof getMessaging> | null = null
const getMessagingInstance = () => {
  if (!messaging) messaging = getMessaging(app)
  return messaging
}

// SEND THE DEVICE FCM TOKEN TO THE SERVER SO THIS WEB DEVICE RECEIVES CALL NOTIFICATIONS.
export const updateFCMToken = async (userId: number, token: string) => {
  return httpRequest('users/fcmtoken/update', 'POST', { token, user_id: userId, platform: 'web' })
}

// A registration returned by register() is usually still INSTALLING the first time a browser
// sees the worker. Subscribing to push at that moment fails with "Subscription failed - no
// active Service Worker", so on a device's first visit the token was never obtained and the
// staff member could not be reached until the page was reloaded. Wait for it to activate.
const ACTIVATION_TIMEOUT_MS = 15000
const waitUntilActive = (registration: ServiceWorkerRegistration): Promise<void> => {
  if (registration.active) return Promise.resolve()
  const worker = registration.installing || registration.waiting
  if (!worker) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = window.setTimeout(resolve, ACTIVATION_TIMEOUT_MS)
    const onChange = () => {
      if (worker.state === 'activated' || worker.state === 'redundant') {
        window.clearTimeout(timer)
        worker.removeEventListener('statechange', onChange)
        resolve()
      }
    }
    worker.addEventListener('statechange', onChange)
  })
}

// Outcome of an attempt to make this device reachable for call alerts, so the screen can tell
// the staff member what happened instead of failing silently.
//  - registered:  the token reached the server; calls will ring here.
//  - denied:      the browser refuses notifications for this site (blocked earlier, or a private
//                 or guest window, where browsers refuse them without asking).
//  - dismissed:   the permission question was closed without an answer.
//  - unsupported: this browser cannot receive web notifications.
//  - failed:      permission is granted but the token could not be obtained or saved.
export type PushRegistrationResult = 'registered' | 'denied' | 'dismissed' | 'unsupported' | 'failed'

const fetchFcmToken = async (userId: number): Promise<PushRegistrationResult> => {
  try {
    // The registration MUST be passed explicitly (roadmap #101): with no registration the
    // messaging library looks for '/firebase-messaging-sw.js' at the site ROOT, which does not
    // exist for a build served from a base path such as /caller-beta/.
    const serviceWorkerRegistration = await getMessagingRegistration()
    if (serviceWorkerRegistration) await waitUntilActive(serviceWorkerRegistration)
    const token = await getToken(getMessagingInstance(), {
      vapidKey: VAPID_KEY,
      ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
    })
    if (!token) return 'failed'
    setStoredFcmToken(token)
    const response: any = await updateFCMToken(userId, token)
    // httpRequest never throws: a transport failure comes back as { success: false } and the
    // API reports its own errors as { status: 'error' }.
    if (!response || response.success === false || response.status === 'error') return 'failed'
    return 'registered'
  } catch (err) {
    console.log('Error getting FCM token:', err)
    return 'failed'
  }
}

const pushSupported = () =>
  typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window

// REGISTER THE TOKEN WHEN NOTIFICATIONS ARE ALREADY ALLOWED. Called when the screen opens, so it
// must NOT ask for permission: browsers penalise permission prompts that no click caused (quiet
// prompt, then an automatic block after a few dismissals), which is how a device ends up unable
// to enable alerts at all. Asking belongs to requestPushPermissionAndRegister, from the button.
export const registerFcmToken = (userId: number): Promise<PushRegistrationResult> => {
  if (!pushSupported()) return Promise.resolve('unsupported')
  if (Notification.permission === 'denied') return Promise.resolve('denied')
  if (Notification.permission !== 'granted') return Promise.resolve('dismissed')
  return fetchFcmToken(userId)
}

// ASK FOR PERMISSION (FROM A CLICK) AND REGISTER THE TOKEN. WITHOUT THIS THE STAFF DEVICE NEVER
// RECEIVES INCOMING-CALL PUSHES.
export const requestPushPermissionAndRegister = async (userId: number): Promise<PushRegistrationResult> => {
  if (!pushSupported()) return 'unsupported'
  let permission: NotificationPermission = Notification.permission
  if (permission !== 'granted') {
    try {
      permission = await Notification.requestPermission()
    } catch (err) {
      console.log('Notification permission request failed', err)
      return 'failed'
    }
  }
  if (permission === 'denied') return 'denied'
  if (permission !== 'granted') return 'dismissed'
  return fetchFcmToken(userId)
}

// REGISTER THE BACKGROUND MESSAGING SERVICE WORKER (SHOWS THE OS NOTIFICATION AND BRIDGES
// THE ACTION BACK TO THE APP WHILE NO TAB IS FOCUSED).
//
// The worker ships in public/, so it is published under the application's base path, NOT at the
// site root: in production this application is served from /caller-beta/. Registering the root
// path requested a file that does not exist, the registration failed, and the bridge that opens
// the in-app incoming-call screen never ran, leaving an operating-system notification the
// application could not act on (roadmap #101). BASE_URL is '/' in development and the deployment
// base in production, and always ends in a slash.
const SERVICE_WORKER_URL = `${import.meta.env.BASE_URL}firebase-messaging-sw.js`

// One shared registration: both the explicit call below and the token request need the same one,
// and registering twice for the same scope is wasteful.
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null

const getMessagingRegistration = (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null)
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .then((registration) => {
        console.log('Messaging service worker registered:', registration.scope)
        return registration
      })
      .catch((err) => {
        console.error('Service worker registration failed:', err)
        // Do not cache the failure: a later attempt (e.g. after a redeploy) should retry.
        registrationPromise = null
        return null
      })
  }
  return registrationPromise
}

export const registerMessagingServiceWorker = () => {
  void getMessagingRegistration()
}

// The page itself can only be watched while it is in front. When the push arrives here instead of
// in the service worker, the browser has decided some window of this site is visible and shows no
// notification of its own — but the visible window may be another one on the same host (the admin
// panel) or this window may sit behind another application. The ringing screen then opened where
// nobody could see it, possibly without sound, and the call was missed with the staff member
// "available" (Nest Flats, 06/10/2026). Whenever this window is not both visible and focused, the
// operating-system notification is raised from here, with the same tag and Accept/Decline buttons
// as the service worker's, so its notificationclick handler takes over unchanged.
const pageIsAttended = () => document.visibilityState === 'visible' && document.hasFocus()

const showIncomingCallNotification = async (data: any) => {
  if (pageIsAttended()) return
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  const registration = await getMessagingRegistration()
  if (!registration || !data?.call_id) return
  let caller: any = {}
  try {
    caller = data.callerDetail ? JSON.parse(data.callerDetail) : {}
  } catch {
    caller = {}
  }
  const title = [caller.qrcode_name, caller.organization_name].filter(Boolean).join(' - ') || t.staffIncomingTitle
  const who = caller.name || caller.email || caller.phone
  try {
    await registration.showNotification(title, {
      body: who ? t.staffIncomingNotificationBody(who) : t.staffIncomingTitle,
      requireInteraction: true,
      icon: new URL('icons/icon-192.png', registration.scope).href,
      badge: new URL('icons/icon-192.png', registration.scope).href,
      tag: data.call_id,
      renotify: true,
      silent: false,
      data,
      actions: [
        { action: 'CALL_ACCEPT', title: t.staffAccept },
        { action: 'CALL_REJECT', title: t.staffReject },
      ],
    } as NotificationOptions)
  } catch (e) {
    console.log('could not show the incoming-call notification', e)
    return
  }
  window.setTimeout(() => closeIncomingCallNotification(data.call_id), (Number(data.maxResponseSeconds) || 45) * 1000)
}

// Close the operating-system notification of a call once it no longer needs an answer.
export const closeIncomingCallNotification = async (callId?: string | null) => {
  if (!callId) return
  try {
    const registration = await getMessagingRegistration()
    if (!registration) return
    const notifications = await registration.getNotifications({ tag: callId })
    notifications.forEach((notification) => notification.close())
  } catch {
    /* no-op */
  }
}

// HANDLE A SILENT/BACKGROUND NOTIFICATION (FOREGROUND PATH). MIRRORS
// processSilentNotification in the previous client: it primes the shared call state and
// routes the staff to the incoming-call screen.
export const processSilentNotification = async (data: any, navigate: (path: string, opts?: any) => void) => {
  const action: string = data?.action

  if (action === 'FCM-LOGOUT-SESSION') {
    staffLogout()
    navigate('/staff')
    return
  }

  if (action === 'FCM-KILL-NOTIFICATION-CALL' && globals.user_id === data.user_id) {
    void closeIncomingCallNotification(data.call_id || globals.call_id)
    globals.callerSocketID = null
    globals.callerUserName = null
    globals.callerDetail = null
    globals.call_id = null
    globals.callStaffData = null
    globals.notification_received_time = null
    globals.userFromNotification = false
    navigate('/staff/dashboard')
    return
  }

  if (action === 'FCM-INCOMING-CALL') {
    globals.callerSocketID = data.body
    globals.callerUserName = data.name
    globals.callerDetail = data.callerDetail ? JSON.parse(data.callerDetail) : null
    globals.call_id = data.call_id
    globals.maxResponseSeconds = data.maxResponseSeconds
    globals.enabledDirectCall = data?.enabledDirectCall
    globals.isThisDefaultStaffNotified = data.isThisDefaultStaffNotified
    globals.callStaffData = data.staffs
    globals.notification_received_time = data.time
    globals.userFromNotification = false
    await updateCallStatusByUserId(data.call_id, 'FCMPR', data.user_id)
    void showIncomingCallNotification(data)

    if (globals.currentScreenName !== 'calling') {
      navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now() } })
    }
  }
}

// FOREGROUND MESSAGES (TAB OPEN): ROUTE THEM THROUGH THE SAME SILENT-NOTIFICATION HANDLER.
export const subscribeForegroundMessages = (navigate: (path: string, opts?: any) => void) => {
  onMessage(getMessagingInstance(), (notification) => {
    const data = notification?.data
    if (data && (typeof data.type !== 'undefined' || typeof data.action !== 'undefined')) {
      processSilentNotification(data, navigate)
    }
  })
}

// PRIME THE SHARED CALL STATE FROM AN INCOMING-CALL NOTIFICATION PAYLOAD AND MARK THE CALL AS
// RECEIVED (FCMPR). SHARED BY THE PLAIN BRIDGE (open the ringing screen) AND THE ACCEPT/REJECT
// NOTIFICATION ACTIONS (#55).
const primeIncomingCallFromPayload = async (payload: any) => {
  globals.callerSocketID = payload.body
  globals.callerUserName = payload.name
  globals.callerDetail = payload.callerDetail ? JSON.parse(payload.callerDetail) : null
  globals.call_id = payload.call_id
  globals.enabledDirectCall = payload?.enabledDirectCall
  globals.isThisDefaultStaffNotified = payload.isThisDefaultStaffNotified
  globals.callStaffData = payload.staffs
  globals.maxResponseSeconds = payload.maxResponseSeconds || globals.maxResponseSeconds
  globals.notification_received_time = payload.time
  globals.userFromNotification = false
  await updateCallStatusByUserId(payload.call_id, 'FCMPR', payload.user_id)
}

// REBUILD AN INCOMING CALL FROM ITS IDENTIFIER ALONE.
//
// The bridge below can only reach an application window that is ALREADY RUNNING. When the
// staff member has no window open, the notification is all there is, and acting on it opens a
// window whose only clue is the call identifier carried in the link. This reads the call
// record and reconstructs the same state the push payload would have primed, so answering
// works from a cold start. Returns false when there is nothing to answer any more — the call
// ended, timed out or another staff member took it — so the caller can say so instead of
// opening a ringing screen for a call that is over.
export const primeIncomingCallFromCallId = async (callId: string): Promise<boolean> => {
  try {
    // The realtime rules require a session; the one established at startup is enough.
    await authReady
    const snapshot = await get(child(ref(database, 'call'), callId))
    const record = snapshot.val()
    if (!record) return false

    // ALREADY RESOLVED? Do not reopen a call that is finished or taken.
    const settled = ['ENDED', 'MISS', 'REJTD', 'ACCPT', 'ANSRD']
    if (settled.indexOf(record.callstatus) !== -1) return false
    if (record.answaredby_fullname) return false

    // Without the guest's connection there is nobody to signal, so answering would strand
    // them on "connecting" — exactly the failure this path exists to fix. The backend writes
    // it onto the record; an older record predating that has none.
    if (!record.callerSocketId) return false

    // The record and the push payload describe the same call with DIFFERENT field names:
    // the record keeps the raw `userinfo` (`qrOrgName`, `qrcodeName`), while the payload
    // carries a `callerDetail` the backend builds from it. The ringing screen reads the
    // payload shape, so the same mapping the backend applies is applied here — otherwise the
    // screen renders with no organisation and no code, which is all the staff member has to
    // recognise who is calling.
    const userinfo = record.userinfo || {}
    globals.callerSocketID = record.callerSocketId
    globals.callerUserName = [userinfo.qrOrgName, userinfo.qrcodeName].filter(Boolean).join(' - ')
    globals.callerDetail = {
      name: userinfo.forminput?.name || '',
      forminput: userinfo.forminput,
      defaultstaff: userinfo.defaultstaff || '',
      lastcalled: userinfo.lastcalled || '',
      lastattendedby: userinfo.lastattendedby || '',
      organization_name: userinfo.qrOrgName,
      qrcode_name: userinfo.qrcodeName,
    }
    globals.call_id = callId
    globals.enabledDirectCall = record.enabledDirectCall
    globals.isThisDefaultStaffNotified = Boolean(record.isDefaultStaffInStaffList)
    // The staff group is on the record too (`staffs`), and the rejection path needs it to
    // notify the other devices. It is serialised because that is the shape the payload
    // delivers and the screen forwards verbatim. An empty list when absent keeps that path
    // working rather than throwing on a parse.
    globals.callStaffData = JSON.stringify(record.staffs || [])
    globals.notification_received_time = String(Date.now())
    globals.userFromNotification = false
    await updateCallStatusByUserId(callId, 'FCMPR', globals.user_id)
    return true
  } catch (e) {
    console.log('could not rebuild the incoming call from its identifier', e)
    return false
  }
}

// BRIDGE MESSAGES POSTED BY THE SERVICE WORKER (BACKGROUND NOTIFICATION INTERACTIONS).
// INCOMING_CALL_ACTION opens the in-app incoming-call screen; INCOMING_CALL_ACCEPT /
// INCOMING_CALL_REJECT (#55) come from the notification's Accept/Decline buttons and drive the
// in-app answer/reject via a navigation flag the ringing screen acts on; STOP_RINGTONE asks the
// screen to stop (another staff answered / the notification was dismissed).
export const subscribeServiceWorkerMessages = (
  navigate: (path: string, opts?: any) => void,
  onStopRingtone: () => void
) => {
  if (!('serviceWorker' in navigator)) return () => {}
  const handler = async (event: MessageEvent) => {
    const data = event.data
    if (!data) return
    const { type, payload, call_id } = data

    if (type === 'STOP_RINGTONE') {
      onStopRingtone()
      return
    }

    if (
      (type === 'INCOMING_CALL_ACTION' ||
        type === 'INCOMING_CALL_ACCEPT' ||
        type === 'INCOMING_CALL_REJECT') &&
      payload &&
      typeof payload === 'object'
    ) {
      await primeIncomingCallFromPayload(payload)
      // The ringing screen reads `autoAccept`/`autoReject` from the navigation state and performs
      // the same accept/reject the in-app buttons do (no protocol duplicated here).
      const autoAccept = type === 'INCOMING_CALL_ACCEPT'
      const autoReject = type === 'INCOMING_CALL_REJECT'
      if (globals.currentScreenName !== 'calling' || autoAccept || autoReject) {
        navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now(), autoAccept, autoReject } })
      }
    } else if (type === 'STOP_RINGTONE_FOR' && call_id && call_id === globals.call_id) {
      onStopRingtone()
    }
  }
  navigator.serviceWorker.addEventListener('message', handler)
  return () => navigator.serviceWorker.removeEventListener('message', handler)
}
